import React, { ReactElement, useCallback, useState } from "react";
import styled from "styled-components";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";
import { ThemeObject } from "@/styles/ThemeObjects";
import { ContentBlockEvents } from "../ContentBlockEvents";
import { StyledUniversalDiv } from "../Universal";

type CarouselTheme = ThemeObject & {};
type StyledCarouselProps = ContentBlockEvents & {
    theme: Partial<CarouselTheme>;
    children?: ReactElement;
    contentBlockId: string;
};

const CarouselItemWrapper = styled.div`
    &:has(.activeBlock) {
        padding-top: 5rem;
        padding-bottom: 5rem;
        > * {
            padding-left: 2rem;
            padding-right: 2rem;
        }
    }
`;

export const defaultCarouselTheme = {
    pl: 1,
    pr: 1,
    pt: 1,
    pb: 1,
    ml: 0,
    mr: 0,
    mt: 0,
    mb: 0,
    backgroundColor: "#ffffffff",
    borderWidth: 1,
    borderColor: "#000000",
    borderRadius: 0,
};

export function StyledCarousel({
    theme,
    onClick,
    isInEditorContext,
    contentBlockId,
    children,
    isSelected,
}: StyledCarouselProps) {
    //get the number of child elements. That's our number of slides.
    const childArray = React.Children.toArray(children);
    const numberOfSlides = childArray?.length || 0;
    const [step, setStep] = useState(0);

    const wrapperRef = React.useRef<HTMLDivElement>(null);

    const scrollToSlide = useCallback(
        (step: number) => {
            if (wrapperRef.current) {
                const child = wrapperRef.current?.children[step];
                if (child) {
                    child.scrollIntoView({
                        behavior: "smooth",
                        block: "nearest",
                    });
                    setStep(step);
                }
            }
        },
        [wrapperRef],
    );

    const advance = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            scrollToSlide(step < numberOfSlides - 1 ? step + 1 : 0);
        },
        [step, numberOfSlides],
    );

    const retreat = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            scrollToSlide(step > 0 ? step - 1 : numberOfSlides - 1);
        },
        [step, numberOfSlides],
    );

    return (
        <StyledUniversalDiv
            {...{ ...defaultCarouselTheme, ...theme }}
            onClick={onClick}
            isInEditorContext={isInEditorContext}
            data-content-block-id={contentBlockId}
            className="carousel group/carousel relative flex w-full items-center justify-center"
        >
            <div
                className="control absolute bottom-0 left-0 top-0 z-[100] flex items-center justify-center
                    px-2 opacity-10 transition-opacity duration-[600ms]
                    ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover/carousel:opacity-100
                    motion-reduce:transition-none"
                role="button"
            >
                <button
                    onClick={retreat}
                    type="button"
                    className="rounded-full bg-slate-500/75 p-2 shadow transition-all duration-[600ms]
                        ease-[cubic-bezier(0.25,0.1,0.25,1.0)] hover:bg-slate-500 hover:scale-110
                        motion-reduce:transition-none"
                >
                    <ChevronLeftIcon className="h-6 w-6 text-white" />
                    <span className="sr-only">Previous</span>
                </button>
            </div>
            <div
                className="control absolute bottom-0 right-0 top-0 z-[100] flex items-center justify-center
                    px-2 opacity-10 transition-opacity duration-[600ms]
                    ease-[cubic-bezier(0.25,0.1,0.25,1.0)] group-hover/carousel:opacity-100
                    motion-reduce:transition-none"
            >
                <button
                    onClick={advance}
                    type="button"
                    className="rounded-full bg-slate-500/75 p-2 shadow transition-all duration-[600ms]
                        ease-[cubic-bezier(0.25,0.1,0.25,1.0)] hover:bg-slate-500 hover:scale-110
                        motion-reduce:transition-none"
                >
                    <ChevronRightIcon className="h-6 w-6 text-white" />
                    <span className="sr-only">Next</span>
                </button>
            </div>
            <CarouselItemWrapper
                ref={wrapperRef}
                className="scrollbar-thumb-shadow-lg scrollbar-track-shadow-lg relative flex w-1 flex-1
                    flex-grow snap-both snap-mandatory items-stretch justify-stretch overflow-auto
                    scrollbar-thin scrollbar-track-slate-500/10 scrollbar-thumb-slate-500/50
                    scrollbar-track-rounded-full scrollbar-thumb-rounded-full
                    has-[#content-block-edit-popover]:pt-20"
            >
                {childArray.map((child, index) => (
                    <div
                        key={index}
                        className="flex w-full min-w-[100%] snap-center items-center justify-center"
                    >
                        {child}
                    </div>
                ))}
            </CarouselItemWrapper>
        </StyledUniversalDiv>
    );
}
